import { createAction } from '@reduxjs/toolkit';
import is from '@sindresorhus/is';
import axios from 'axios';

import { browserRouter } from '@/browserRouter';
import createAsyncThunk from '@/redux/createAsyncThunk';
import { useDialogStore } from '@/stores/dialog';
import {
  FIRST_ONBOARDING_STEP_FULL_URL,
  FIRST_ONBOARDING_STEP_URL,
} from '@chat/aibots/containers/Onboarding/constants';
import {
  V2_FIRST_ONBOARDING_STEP_FULL_URL,
  V2_FIRST_ONBOARDING_STEP_URL,
} from '@chat/aibots/containers/OnboardingV2/constants';
import { oauthGoogle, verifyAuth } from '@common/api/authentication';
import {
  deleteSamlConfiguration,
  exportOrganizationMembersList,
  transferOwner,
  updateOrganization,
  updateSamlConfiguration,
} from '@common/api/organizations';
import { DialogType } from '@common/containers/dialogs/DialogType';
import { verifyIsAiAgentOrganization } from '@common/utils/verifyIsAiAgentOrganization';
import { PredefinedRoles } from '@constants';
import { toast } from '@feather/components/notification/toast';
import { LocalStorage } from '@utils';
import getErrorMessage from '@utils/getErrorMessage';
import logException from '@utils/logException';
import saveAs from '@utils/saveAs';
import toastBadRequestWarning from '@utils/toastBadRequestWarning';
import { getCurrentPathWithQueryString, getNextPath } from '@utils/urlParams';

const orgCreationExceptionRoute = [
  '/application-id/ai-chatbots/onboarding',
  FIRST_ONBOARDING_STEP_URL,
  FIRST_ONBOARDING_STEP_FULL_URL,
  V2_FIRST_ONBOARDING_STEP_URL,
  V2_FIRST_ONBOARDING_STEP_FULL_URL,
];

const shouldRedirectToOrgCreation = (organizations: readonly Organization[]) => {
  const shouldRedirect =
    orgCreationExceptionRoute.every((url) => !location.pathname.includes(url)) && is.emptyArray(organizations);
  return shouldRedirect;
};

type SSOConfigurationFormParams = {
  entity_id: string;
  idp_cert: string;
  idp_url: string;
  sso_enforcing: boolean;
  jit_provisioning: boolean;
  sso_default_role: string;
};

export const SessionActions = {
  authFetchingSet: createAction('common/session/authFetchingSet'),
  authFetchingUnset: createAction('common/session/authFetchingUnset'),

  signedIn: createAction<{
    token?: string;
    two_factor_authentication_required?: boolean;
    /**
     * @warning This attr is for testing purpose only.
     */
    authenticated?: boolean;
  }>('common/session/signedIn'),

  accountActivated: createAction('common/session/accountActivated'),

  verifyAuthentication: createAsyncThunk<void, void | { invited?: boolean; next?: string }>(
    'common/session/verifyAuthentication',
    async (payload, { dispatch, rejectWithValue }) => {
      try {
        const { data } = await verifyAuth();

        // Right after the user signed up, set his role Owner.
        const role: MemberRole =
          data.organizations.length === 0
            ? {
                id: 0,
                name: PredefinedRoles.OWNER,
                permissions: [
                  'organization.general.all',
                  'organization.applications.all',
                  'organization.members.all',
                  'organization.billing.all',
                  'organization.security.all',
                  'application.overview.view',
                  'moderation.openChannel.all',
                  'moderation.groupChannel.all',
                  'moderation.supergroupChannel.all',
                  'chat.announcements.all',
                  'chat.dataExport.all',
                  'chat.messageSearch.all',
                  'application.users.all',
                  'chat.analytics.view',
                  'application.settings.all',
                  'support.technical',
                ],
                is_predefined: true,
                description: '',
                is_application_access_control: false,
                application_access_controls: [],
                application_access_controls_details: [],
                pii_access: 'read_write',
              }
            : data.role;

        dispatch(SessionActions.authenticated({ ...data, role }));

        // enforce 2FA if user doesn't have 2FA enabled
        if (data.organizations.length > 0) {
          const currentOrg = data.organizations[0];
          if (currentOrg.enforce_two_factor_authentication && !data.user.two_factor_authentication) {
            browserRouter.navigate(`/auth/two_factor_registration?next=${payload?.next || '/'}`);
            return;
          }
        }

        // no org check
        if (shouldRedirectToOrgCreation(data.organizations)) {
          browserRouter.navigate(`/onboarding/organization?${payload?.next ? `next=${payload?.next}` : ''}`);
          return;
        }
        // next url check
        if (payload?.next) {
          browserRouter.navigate(getNextPath());
          return;
        }
        // ai agent dashboard check
        const aiAgentDashboardType = verifyIsAiAgentOrganization(data.organizations);

        // when user have to be landed on ai agent dashboard
        if (
          aiAgentDashboardType === 'AI_AGENT_ONLY' ||
          (aiAgentDashboardType === 'AI_AGENT_AND_DESK' && LocalStorage.get('productMode') === 'aiagent') ||
          (aiAgentDashboardType === 'AI_AGENT_AND_CHAT' && LocalStorage.get('productMode') === 'aiagent')
        ) {
          if (
            role.name === PredefinedRoles.OWNER ||
            role.name === PredefinedRoles.ADMIN ||
            (role.ai_agent_permission_sets_details?.length ?? 0) > 0
          ) {
            browserRouter.navigate('/ai-agent');
            return;
          }
          // no ai agent permission sets, so redirect to chat product -> should be no permission page
          browserRouter.navigate('/');
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const { status, data } = error.response || {};
          if (status === 400 && data) {
            dispatch(SessionActions.unauthenticated());
            toast.error({ message: (data as { message: string }).message });
            browserRouter.navigate(`/auth/signin?next=${getCurrentPathWithQueryString()}`);
          } else {
            toast.error({ message: getErrorMessage(error) });
          }
          return rejectWithValue(error);
        }

        logException(error);
        dispatch(SessionActions.authenticationFailed(error));
        return rejectWithValue(error);
      }
    },
  ),

  verificationMailSent: createAction('common/session/verificationMailSent'),
  emailVerified: createAction('common/session/emailVerified'),

  authenticated: createAction<AuthenticationResponse>('common/session/authenticated'),
  authenticationFailed: createAction('common/session/authenticationFailed'),
  unauthenticated: createAction('common/session/unauthenticated'),

  // social
  oauthGoogle: createAsyncThunk<
    void,
    {
      id_token: string;
      next?: string; // JSON string
    }
  >('common/session/oauthGoogle', async (payload, { dispatch, rejectWithValue }) => {
    const { id_token, next } = payload;

    try {
      const { data } = await oauthGoogle({ idToken: id_token });
      const { code } = data;

      switch (code) {
        case 'type.auth.google.user.not_exist': {
          browserRouter.navigate(`/auth/signup?sso_token=${data.token}&sso_type=google`, {
            state: {
              email: data.email,
            },
          });
          break;
        }

        case 'type.auth.google.success': {
          let to = '/';
          if (data.two_factor_registration_required) {
            dispatch(SessionActions.signedIn(data));
            to = `/auth/two_factor_registration${payload.next === '' ? '' : `?next=${payload.next}`}`;
          } else if (data.two_factor_authentication_required) {
            dispatch(
              SessionActions.signedIn({
                ...data,
                two_factor_authentication_required: true,
              }),
            );
            to = `/auth/two_factor${next ? `?next=${next}` : ''}`;
          } else if (next) {
            to = next;
          }
          browserRouter.navigate(to);
          break;
        }

        default: {
          break;
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toastBadRequestWarning(error);
        browserRouter.navigate('/auth/signin');
        return rejectWithValue(error);
      }

      logException(error);
      return rejectWithValue(error);
    }
  }),

  twoFactorAuthenticationSet: createAction<{ two_factor_authentication: boolean }>(
    'common/session/twoFactorAuthenticationSet',
  ),

  organizationCreated: createAction<{ organizations: Organization[] }>('common/organizations/organizationCreated'),
  organizationUpdated: createAction<Partial<Organization>>('common/organizations/organizationUpdated'),

  updateOrganizationName: createAsyncThunk<void, { name: Organization['name'] }, { rejectValue: string | null }>(
    'common/organizations/updateOrganizationName',
    async (payload, { dispatch, getState, rejectWithValue }) => {
      const { uid } = getState().session.currentOrganization;

      try {
        const { data } = await updateOrganization(uid, { name: payload.name });

        dispatch(SessionActions.organizationUpdated(data));
      } catch (error) {
        if (axios.isAxiosError(error)) {
          return rejectWithValue(getErrorMessage(error));
        }

        logException(error);
        return rejectWithValue(getErrorMessage(error));
      }
    },
  ),
  updateOrganizationSlugName: createAsyncThunk<
    Organization,
    SSOConfigurationFormParams & { slug_name: string },
    { rejectValue: string | null }
  >('common/organizations/updateOrganizationSlugName', async (payload, { dispatch, getState, rejectWithValue }) => {
    const { uid } = getState().session.currentOrganization;
    const { slug_name, ...samlConfigurationPayload } = payload;

    try {
      const { data } = await updateOrganization(uid, { slug_name });

      dispatch(SessionActions.updateSamlConfiguration(samlConfigurationPayload));
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail: string })?.detail;
        if (detail) {
          return rejectWithValue(detail);
        }
        return rejectWithValue(getErrorMessage(error));
      }

      logException(error);
      return rejectWithValue(getErrorMessage(error));
    }
  }),
  updateSamlConfiguration: createAsyncThunk<
    UpdateSamlConfigurationResponse,
    SSOConfigurationFormParams,
    { rejectValue: string | null }
  >('common/organizations/updateSamlConfiguration', async (payload, { getState, rejectWithValue }) => {
    const { uid, slug_name } = getState().session.currentOrganization;

    try {
      const { data } = await updateSamlConfiguration(uid, payload);

      useDialogStore.getState().show({
        dialogTypes: DialogType.SamlOneMoreStepToGo,
        dialogProps: {
          organizationKey: slug_name,
        },
      });
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          window.intl.formatMessage({ id: 'common.authentication.sso.error.samlConfigurationFail' }),
        );
      }

      logException(error);
      return rejectWithValue(getErrorMessage(error));
    }
  }),
  updateSamlConfigurationReset: createAction('common/organizations/updateSamlConfigurationReset'),

  deleteSamlConfiguration: createAsyncThunk<void>(
    'common/organizations/deleteSamlConfiguration',
    async (_, { getState }) => {
      const { uid } = getState().session.currentOrganization;

      try {
        await deleteSamlConfiguration(uid);

        useDialogStore.getState().hide();
        toast.success({ message: window.intl.formatMessage({ id: 'alerts.samlConfigurationDeleted' }) });
      } catch (error) {
        toastBadRequestWarning(error);
      }
    },
  ),

  transferOwner: createAsyncThunk<void, TransferOwnerPayload>(
    'common/organizations/transferOwner',
    async (payload, { rejectWithValue }) => {
      try {
        await transferOwner({
          organization_uid: payload.organization_uid,
          email: payload.email,
        });

        useDialogStore.getState().hide();
        toast.success({ message: 'Transferring Owner is completed. You should sign in again.' });
        location.href = '/auth/signout';
      } catch (error) {
        if (axios.isAxiosError(error)) {
          toastBadRequestWarning(error);
          return rejectWithValue(error);
        }

        logException(error);
        return rejectWithValue(error);
      }
    },
  ),

  exportOrganizationMembers: createAsyncThunk<void>(
    'common/organizations/exportOrganizationMembers',
    async (_, { getState }) => {
      const { uid } = getState().session.currentOrganization;

      try {
        const { data } = await exportOrganizationMembersList(uid);

        saveAs({ data, filename: 'organization_member_list.csv' });
      } catch (error) {
        logException(error);
      }
    },
  ),

  userUpdated: createAction<AuthUser>('common/session/userUpdated'),
  emailChanged: createAction<{ new_email: string }>('common/session/userEmailChanged'),
  passwordChanged: createAction<{ token: string; user: AuthUser }>('common/session/passwordChanged'),
};
